/**
 * PupilMemoryCard — ADI Operational Continuity Sprint,
 * "Remember This Pupil" Phase 1 (Feb 28, 2026).
 *
 * Calm sticky-note style instructor-only memory for a single
 * pupil. Helps the instructor walk into the lesson with the
 * right human context — personality, confidence, communication
 * style, what helps them, what overwhelms them, continuity
 * reminders.
 *
 * Doctrine pins baked in:
 *
 *   • Instructor surface ONLY. The card is mounted only inside
 *     PupilProfile.js. There is no learner route, no learner
 *     read of this endpoint, no display anywhere a pupil can see.
 *
 *   • Calm sticky-note feel — single free-form textarea, no tags,
 *     no risk badges, no AI generation, no scoring, no analytics.
 *     The "what helps them / what overwhelms them / continuity"
 *     hints are PLACEHOLDER prompts ONLY — never enforced fields,
 *     never separate metrics.
 *
 *   • Lightweight — capped at 1200 characters (sticky-note
 *     territory) and reads in under ~30 seconds.
 *
 *   • Two-state UI: when there is no note yet, render a small
 *     calm "Add a memory note" affordance. When a note exists,
 *     render the calm pinned sticky card. No noisy "empty
 *     dashboard" placeholders.
 *
 *   • No analytics / profiling / behavioural language. No
 *     "behaviour", no "risk", no "personality type". The label
 *     is literally "Remember about this pupil".
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { StickyNote, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_NOTE_LEN = 1200;

const PLACEHOLDER = [
  "How they learn best.",
  "What helps them feel calm.",
  "What overwhelms them.",
  "Continuity reminders for next lesson.",
].join("\n");

function fmtUpdated(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    const day = Math.floor(h / 24);
    if (day < 14) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

export default function PupilMemoryCard({ pupilId }) {
  const [note, setNote] = useState("");
  const [originalNote, setOriginalNote] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [ready, setReady] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!pupilId) return undefined;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/instructor/pupil-memory/${pupilId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const incoming = (data?.note || "").toString();
        setNote(incoming);
        setOriginalNote(incoming);
        setUpdatedAt(data?.updated_at || null);
      } catch {
        // Calm doctrine: silent on fetch failure. The instructor's
        // operational truth is on PupilProfile; memory is auxiliary.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pupilId]);

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/api/instructor/pupil-memory/${pupilId}`,
        { note: note.slice(0, MAX_NOTE_LEN) },
        { withCredentials: true },
      );
      setOriginalNote(data?.note ?? "");
      setUpdatedAt(data?.updated_at || null);
      setEditing(false);
      toast.success("Saved");
    } catch {
      toast.error("Couldn't save just now");
    } finally {
      setSaving(false);
    }
  }, [pupilId, note, saving]);

  if (!ready) return null;

  const hasNote = (originalNote || "").trim().length > 0;
  const dirty = (note || "") !== (originalNote || "");
  const remaining = MAX_NOTE_LEN - (note || "").length;

  // Empty + not editing → tiny calm affordance to start a note.
  if (!hasNote && !editing) {
    return (
      <button
        type="button"
        data-testid="pupil-memory-add-trigger"
        onClick={() => setEditing(true)}
        className="w-full flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-transparent hover:bg-slate-50 dark:hover:bg-slate-900/40 text-left transition-colors"
      >
        <StickyNote className="w-3.5 h-3.5 text-slate-400 flex-none" aria-hidden="true" />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Add a memory note for this pupil
        </span>
      </button>
    );
  }

  return (
    <section
      data-testid="pupil-memory-card"
      aria-labelledby="pmc-title"
      className="rounded-2xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/30 p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-2">
        <StickyNote
          className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300"
          aria-hidden="true"
        />
        <p
          id="pmc-title"
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800 dark:text-amber-300"
        >
          Remember about this pupil
        </p>
        {!editing && updatedAt && (
          <span
            data-testid="pupil-memory-updated-at"
            className="ml-auto text-[10px] text-amber-700/70 dark:text-amber-300/60"
          >
            {fmtUpdated(updatedAt)}
          </span>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            data-testid="pupil-memory-textarea"
            value={note}
            maxLength={MAX_NOTE_LEN}
            onChange={(e) => setNote(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={5}
            className="w-full text-sm leading-relaxed text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40 border border-amber-200 dark:border-amber-900/30 rounded-xl p-3 resize-y focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <div className="flex items-center justify-between gap-3">
            <span
              data-testid="pupil-memory-remaining"
              className="text-[10px] text-amber-700/70 dark:text-amber-300/60"
            >
              {remaining} characters left
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="pupil-memory-cancel"
                onClick={() => {
                  setNote(originalNote || "");
                  setEditing(false);
                }}
                className="text-xs px-3 py-1.5 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-amber-100/60 dark:hover:bg-amber-900/30"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="pupil-memory-save"
                onClick={save}
                disabled={!dirty || saving}
                className="text-xs px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p
            data-testid="pupil-memory-display"
            className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-wrap"
          >
            {originalNote}
          </p>
          <div className="flex items-center justify-end">
            <button
              type="button"
              data-testid="pupil-memory-edit"
              onClick={() => setEditing(true)}
              className="text-xs text-amber-800 dark:text-amber-300 hover:underline"
            >
              Edit note
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
